import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { WaitlistForm } from "@/components/landing/WaitlistForm";
import { Countdown } from "@/components/landing/Countdown";

const API = "/api";
const BRANDING_API = "/api/branding";

const FEATURES = [
  ["01", "Confidence Score 0–100", "Struktur pasar, SNR, Fibonacci, price action, tren, berita, spread — dihitung dari candle real tiap 30 detik."],
  ["02", "Risk Engine", "Risiko 1% per trade, batas rugi harian 3%, stop setelah 3 rugi beruntun. NO TRADE beserta alasannya selalu tercatat."],
  ["03", "News Filter Otomatis", "Kalender ekonomi real (ForexFactory) memblokir entry ±15 menit sebelum & sesudah berita high-impact."],
  ["04", "Paper Trading 24/7", "Robot simulasi berjalan terus di server. History, PNL, dan equity curve tercatat penuh di jurnal."],
];

const TickerCard = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const load = () =>
      axios
        .get(`${API}/public/ticker`)
        .then((r) => setItems(r.data.items))
        .catch(() => {});

    load();

    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      className="border border-white/10 bg-[#0d1219]"
      data-testid="live-ticker-card"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mute-dim">
          Live Market
        </span>

        <span className="flex items-center gap-1.5 font-mono text-[9px] font-bold text-buy">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-buy" />
          DATA REAL
        </span>
      </div>

      <div className="divide-y divide-white/5">
        {items.length === 0 ? (
          <p className="px-4 py-6 font-mono text-xs text-mute-dim">
            memuat feed pasar...
          </p>
        ) : (
          items.map((s) => (
            <div
              key={s.symbol}
              className="flex items-center justify-between px-4 py-2.5"
              data-testid={`ticker-${s.symbol.toLowerCase()}`}
            >
              <span className="font-mono text-sm font-bold text-white">
                {s.symbol}
              </span>

              <span
                className={`font-mono text-[11px] font-bold ${
                  s.direction === "BUY"
                    ? "text-buy"
                    : s.direction === "SELL"
                    ? "text-sell"
                    : "text-mute-dim"
                }`}
              >
                {s.direction || s.decision}
              </span>

              <span className="flex items-center gap-2">
                <span className="h-1 w-14 bg-white/10">
                  <span
                    className={`block h-full ${
                      s.total >= 70
                        ? "bg-buy"
                        : s.total >= 60
                        ? "bg-warn"
                        : "bg-sell"
                    }`}
                    style={{ width: `${s.total}%` }}
                  />
                </span>

                <span className="w-6 text-right font-mono text-[11px] text-mute">
                  {s.total}
                </span>
              </span>

              <span
                className={`w-14 text-right font-mono text-[11px] ${
                  (s.change_pct ?? 0) >= 0 ? "text-buy" : "text-sell"
                }`}
              >
                {(s.change_pct ?? 0) >= 0 ? "+" : ""}
                {s.change_pct}%
              </span>
            </div>
          ))
        )}
      </div>

      <p className="border-t border-white/5 px-4 py-2 font-mono text-[9px] text-mute-dim">
        &gt; harga &amp; skor real · harga diperbarui tiap ±8 detik, analisa tiap 30 detik
      </p>
    </div>
  );
};

export default function LandingSimple() {
  const [branding, setBranding] = useState({
    brand_name: "NAKED CHART ID",
    logo_url: "",
  });

  useEffect(() => {
    axios
      .get(BRANDING_API)
      .then((r) => setBranding(r.data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.post(`${API}/public/visit`).catch(() => {});
  }, []);

  return (
    <div
      className="min-h-screen bg-[#080b10] font-mono text-white"
      data-testid="landing-simple"
    >
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <a
            href="/"
            className="flex items-center gap-2.5"
            data-testid="landing-logo"
          >
            <img
              src={branding.logo_url || "/naked-chart-logo.png"}
              alt={branding.brand_name || "NAKED CHART ID"}
              className="h-9 w-auto max-w-[72px] object-contain shrink-0"
            />

            <span className="font-display text-sm font-bold">
              {branding.brand_name || "NAKED CHART ID"}
            </span>
          </a>

          <a
            href="/masuk"
            data-testid="landing-login-btn"
            className="group flex items-center gap-1.5 border border-buy bg-buy/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-buy transition-colors duration-200 hover:bg-buy hover:text-ink"
          >
            Masuk Platform
            <ArrowUpRight
              size={14}
              className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5">
        <section
          className="grid items-center gap-12 py-16 sm:py-24 lg:grid-cols-2"
          data-testid="landing-hero"
        >
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="text-[10px] uppercase tracking-[0.3em] text-mute-dim"
            >
              // AI trading terminal — price action murni
            </motion.p>

            <h1 className="mt-6 font-display text-4xl font-black leading-[1.02] sm:text-5xl">
              {["BACA MARKET.", "BUKAN", "INDIKATOR."].map((l, i) => (
                <span key={l} className="block overflow-hidden">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{
                      delay: 0.15 + i * 0.12,
                      duration: 0.8,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className={`block ${
                      i === 0 ? "text-buy" : "text-white"
                    }`}
                  >
                    {l}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="mt-6 max-w-md text-sm leading-relaxed text-mute"
            >
              Engine menganalisis XAUUSD, EURUSD, GBPUSD, USDJPY, dan BTCUSD
              dari data pasar real — lengkap dengan Entry, SL, TP, dan alasan di
              balik setiap keputusan. Tanpa janji profit.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-8"
            >
              <WaitlistForm id="simple" compact />

              <div className="mt-6 flex items-center gap-4">
                <span className="text-[9px] uppercase tracking-[0.25em] text-mute-dim">
                  akses awal dibuka dalam
                </span>
                <Countdown />
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <TickerCard />
          </motion.div>
        </section>

        <section
          className="border-t border-white/10"
          data-testid="landing-features"
        >
          {FEATURES.map(([num, title, desc], i) => (
            <motion.div
              key={num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.05, duration: 0.6 }}
              className="grid gap-2 border-b border-white/10 py-6 transition-colors duration-300 hover:bg-white/[0.02] sm:grid-cols-[80px_240px_1fr] sm:items-baseline sm:gap-6"
              data-testid={`feature-row-${num}`}
            >
              <span className="text-xs text-buy">{num}</span>
              <h3 className="font-display text-base font-bold text-white">
                {title}
              </h3>
              <p className="text-xs leading-relaxed text-mute">{desc}</p>
            </motion.div>
          ))}
        </section>

        <section className="py-14 text-center" data-testid="landing-cta">
          <p className="text-[10px] uppercase tracking-[0.3em] text-mute-dim">
            // gratis untuk akses awal
          </p>

          <a
            href="/masuk"
            data-testid="landing-cta-btn"
            className="mt-5 inline-flex items-center gap-2 border border-buy bg-buy px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-ink transition-colors duration-200 hover:bg-buy/85"
          >
            Mulai Sekarang
            <ArrowUpRight size={16} />
          </a>

          <p
            className="mx-auto mt-8 max-w-xl text-[10px] leading-relaxed text-mute-dim"
            data-testid="landing-disclaimer"
          >
            Peringatan risiko: trading forex, emas, dan kripto berisiko tinggi.
            Platform ini adalah perangkat analisis &amp; eksekusi — bukan
            penasihat keuangan, tidak menjanjikan profit. Live trading terkunci
            secara default dan membutuhkan aktivasi eksplisit.
          </p>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-5 text-[10px] text-mute-dim">
          <span>© 2026 NAKED CHART ID</span>
          <span>
            data real Yahoo Finance · kalender real ForexFactory · audit penuh
          </span>
        </div>
      </footer>
    </div>
  );
}
